import type { UpgradeKind } from "../../game/upgrades/definitions";
import type { UpgradeRowView } from "../../game/upgrades/view";
import type { GameSnapshot } from "../../game/runtime/types";
import type { OrbitForgeRuntime } from "../../game/runtime/runtime";
import { formatCompact } from "../../utils/math";
import { createGameIcon, createIcon, type GameIconName } from "../icon";

const upgradeIcons: Record<UpgradeKind, GameIconName> = {
  MaxCores: "maxCores",
  CoreValue: "coreValue",
  ChargeBonus: "chargeBonus",
  ShellSpeed: "shellSpeed",
  GateWidth: "gateWidth",
  GateCount: "gateCount",
  ExtraShell: "extraShell",
  CollectorAmp: "collectorAmp",
  OverchargedCore: "overchargedCore",
  PolaritySync: "polaritySync",
  CriticalDischarge: "criticalDischarge",
  OfflineReactor: "offlineReactor",
  ShellLens: "shellLens"
};

export function createForgePanel(runtime: OrbitForgeRuntime): HTMLElement {
  const panel = document.createElement("section");
  panel.className = "of-panel of-forge-panel";
  panel.setAttribute("aria-label", "Forge upgrades");

  const title = document.createElement("h2");
  title.className = "of-panel-title";
  title.textContent = "FORGE";

  const list = document.createElement("div");
  list.className = "of-upgrade-list";

  const header = document.createElement("div");
  header.className = "of-panel-header";
  header.append(title);
  panel.append(header, list);

  let seenFeedbackId = 0;
  let renderedSignature = "";
  let latestSnapshot: GameSnapshot | null = null;
  let wasOpen = false;

  const requestRowsRender = () => {
    if (!latestSnapshot) {
      return;
    }

    const signature = rowSignature(latestSnapshot);
    if (signature === renderedSignature) {
      return;
    }

    renderRows(list, latestSnapshot, runtime);
    renderedSignature = signature;
  };

  runtime.subscribe((snapshot) => {
    const isOpen = snapshot.activePanel === "forge";
    if (isOpen !== wasOpen) {
      panel.classList.toggle("is-open", isOpen);
      wasOpen = isOpen;
    }
    if (!isOpen) {
      return;
    }

    latestSnapshot = snapshot;
    requestRowsRender();
    seenFeedbackId = applyPanelFeedback(list, snapshot, seenFeedbackId);
  });

  return panel;
}

function rowSignature(snapshot: GameSnapshot): string {
  return [
    snapshot.save.stats.Credits.toFixed(0),
    ...snapshot.forge.rows.map((row) =>
      [
        row.kind,
        row.level,
        row.locked ? 1 : 0,
        row.maxed ? 1 : 0,
        row.affordable ? 1 : 0,
        row.price
      ].join(":")
    )
  ].join("|");
}

function renderRows(list: HTMLElement, snapshot: GameSnapshot, runtime: OrbitForgeRuntime): void {
  const rows = snapshot.forge.rows.map((row) => createUpgradeRow(row, runtime));
  list.replaceChildren(...rows);
}

function createUpgradeRow(row: UpgradeRowView, runtime: OrbitForgeRuntime): HTMLElement {
  const item = document.createElement("article");
  item.className = "of-upgrade-row";
  item.dataset.upgradeKind = row.kind;
  item.classList.toggle("is-locked", row.locked);
  item.classList.toggle("is-affordable", row.affordable);
  item.classList.toggle("is-unaffordable", !row.locked && !row.maxed && !row.affordable);
  item.classList.toggle("is-maxed", row.maxed);

  const icon = document.createElement("span");
  icon.className = "of-upgrade-icon";
  icon.append(createGameIcon(upgradeIcons[row.kind], row.label));

  const details = document.createElement("div");
  details.className = "of-upgrade-details";

  const nameLine = document.createElement("div");
  nameLine.className = "of-upgrade-name-line";

  const name = document.createElement("strong");
  name.className = "of-upgrade-name";
  name.textContent = row.label;

  const level = document.createElement("span");
  level.className = row.locked ? "of-upgrade-level of-upgrade-requirement-line" : "of-upgrade-level";
  if (row.locked) {
    const lockIcon = createIcon("locked", "Locked");
    lockIcon.classList.add("of-upgrade-lock-badge");
    lockIcon.setAttribute("aria-hidden", "true");
    lockIcon.removeAttribute("aria-label");
    lockIcon.removeAttribute("role");
    const requirement = document.createElement("span");
    requirement.className = "of-upgrade-requirement-text";
    requirement.textContent = `REQ LVL ${row.unlockLevel}`;
    level.append(lockIcon, requirement);
  } else {
    level.textContent = `LV ${row.level}/${row.maxLevel}`;
  }

  nameLine.append(name, level);

  const effects = document.createElement("div");
  effects.className = "of-upgrade-effects";
  effects.append(effectChip("NEXT", row.nextEffect));
  details.append(nameLine, effects);

  const actions = document.createElement("div");
  actions.className = "of-upgrade-actions";

  const price = document.createElement("span");
  price.className = "of-upgrade-price";
  price.textContent = row.maxed ? "MAX" : `${formatCompact(row.price)} CR`;

  const buy = document.createElement("button");
  buy.className = "of-upgrade-buy";
  buy.type = "button";
  buy.textContent = row.maxed ? "MAX" : "BUY";
  buy.setAttribute("aria-disabled", row.affordable ? "false" : "true");
  buy.addEventListener("click", () => runtime.dispatch({ type: "buy-upgrade", kind: row.kind }));

  if (!row.locked) {
    actions.append(price);
  }
  actions.append(buy);
  item.append(icon, details, actions);
  return item;
}

function effectChip(label: string, value: string): HTMLElement {
  const chip = document.createElement("span");
  chip.className = "of-effect-chip";

  const tag = document.createElement("span");
  tag.className = "of-effect-tag";
  tag.textContent = label;

  const text = document.createElement("span");
  text.className = "of-effect-value";
  text.textContent = value;

  chip.append(tag, text);
  return chip;
}

function applyPanelFeedback(list: HTMLElement, snapshot: GameSnapshot, seenFeedbackId: number): number {
  let highestFeedbackId = seenFeedbackId;
  for (const event of snapshot.feedback) {
    if (event.id <= seenFeedbackId) {
      continue;
    }

    highestFeedbackId = Math.max(highestFeedbackId, event.id);
    if (!event.target?.startsWith("upgrade-")) {
      continue;
    }

    const kind = event.target.slice("upgrade-".length);
    const row = list.querySelector<HTMLElement>(`.of-upgrade-row[data-upgrade-kind="${kind}"]`);
    if (!row) {
      continue;
    }

    const className = event.kind === "denied" ? "is-denied" : "is-pulsing";
    row.classList.add(className);
    window.setTimeout(() => row.classList.remove(className), 320);
  }
  return highestFeedbackId;
}
